import React, { createContext, useContext, useState, useEffect } from 'react';
import { Theme, createTheme } from '@mui/material/styles';

type ThemeContextType = {
  isDarkMode: boolean;
  toggleTheme: () => void;
  theme: Theme;
};

// Define theme settings
const getTheme = (isDarkMode: boolean): Theme => createTheme({
  palette: {
    mode: isDarkMode ? 'dark' : 'light',
    primary: {
      main: '#8B5A2B', // Rich brown
      light: '#B8860B', // Dark goldenrod
      dark: '#654321', // Dark brown
    },
    secondary: {
      main: '#D2691E', // Chocolate
      light: '#E9967A', // Dark salmon
      dark: '#A0522D', // Sienna
    },
    background: {
      default: isDarkMode ? '#121212' : '#F8F4E9', // Light cream for light mode
      paper: isDarkMode ? '#1E1E1E' : '#FFFFFF',
    },
    text: {
      primary: isDarkMode ? '#FFFFFF' : '#2C1810', // Dark brown for text
      secondary: isDarkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(44, 24, 16, 0.7)',
    },
  },
  typography: {
    fontFamily: '"Playfair Display", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 500,
    },
    h2: {
      fontWeight: 500,
    },
    h3: {
      fontWeight: 500,
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: isDarkMode ? '#2C1810' : '#8B5A2B', // Rich brown
          color: isDarkMode ? '#FFFFFF' : '#F8F4E9', // Light cream
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        contained: {
          background: 'linear-gradient(45deg, #8B5A2B 30%, #D2691E 90%)',
          color: '#F8F4E9', // Light cream
          '&:hover': {
            background: 'linear-gradient(45deg, #B8860B 30%, #E9967A 90%)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: isDarkMode ? '#1E1E1E' : '#FFFFFF',
          borderRadius: 12,
          '&:hover': {
            boxShadow: `0 0 15px ${isDarkMode ? 'rgba(139, 90, 43, 0.3)' : 'rgba(139, 90, 43, 0.5)'}`,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: isDarkMode ? 'rgba(139, 90, 43, 0.1)' : 'rgba(139, 90, 43, 0.2)',
          color: isDarkMode ? '#B8860B' : '#8B5A2B',
          '&:hover': {
            backgroundColor: isDarkMode ? 'rgba(139, 90, 43, 0.2)' : 'rgba(139, 90, 43, 0.3)',
          },
        },
      },
    },
  },
});

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : false; // Default to light theme
  });

  const theme = getTheme(isDarkMode);

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme, theme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}; 